import { ReactNode, useEffect, useMemo } from 'react'
import {
    Box,
    Skeleton,
    Icon,
    IconButton,
    Grid,
    Stack,
    Breakpoint
} from '@mui/material'
import { ifEnter } from '../utils/dom'
import { useHistory } from 'react-router-dom'
import { useIntl, defineMessages } from 'react-intl'
import {
    useCrossAppNavigation,
    useHasReferrer,
    useSetPageTitle
} from '@thriveglobal/thrive-web-core'
import {
    CoreAlert,
    LeafIcon,
    PageLayout,
    useTheme,
    CoreTypography
} from '@thriveglobal/thrive-web-leafkit'
import { usePageContext } from '../utils'
import updateSectionStyles from '../utils/updateSectionStyles'

const intlMessages = defineMessages({
    learnDocumentTitle: {
        defaultMessage: '{title} | Learn | Thrive',
        description: `The page title for Thrive Learn`
    }
})

export interface BackNavigation {
    title: string
    route?: string
    action?: () => void
}
interface PageProps {
    showLoading?: boolean
    title?: string
    subTitle?: string
    children?: ReactNode
    backNavigation?: BackNavigation | null
    action?: ReactNode
    iconAction?: ReactNode
    docTitle?: string
    isOtherMicroFe?: boolean
    sx?: Record<string, any>
    maxWidth?: Breakpoint
}

export default function Page({
    showLoading,
    title,
    subTitle,
    children,
    backNavigation: _backNavigation = null,
    action,
    iconAction,
    docTitle,
    isOtherMicroFe = false,
    maxWidth = 'lg',
    sx
}: PageProps) {
    const intl = useIntl()
    const theme = useTheme()
    const history = useHistory()
    const hasReferrer = useHasReferrer(['search', 'journeys', 'challenges'])
    const navigate = useCrossAppNavigation()

    const { messages = [], removeMessage } = usePageContext()

    useSetPageTitle(
        intl.formatMessage(intlMessages.learnDocumentTitle, {
            title: docTitle || title
        })
    )

    useEffect(() => {
        updateSectionStyles(isOtherMicroFe)
    }, [isOtherMicroFe])

    const backNavigation = useMemo<BackNavigation | null>(() => {
        const historyState: any = history.location?.state

        if (historyState && hasReferrer) {
            return {
                title: historyState.referrerTitle,
                action: () => navigate(historyState.from)
            }
        }

        if (
            historyState &&
            historyState.backNavigation?.route &&
            !hasReferrer
        ) {
            return {
                title: historyState.backNavigation.title,
                action: () => navigate(historyState.backNavigation.route)
            }
        }

        return _backNavigation
    }, [_backNavigation, hasReferrer, history, navigate])

    return (
        <PageLayout maxWidth={maxWidth}>
            <Box
                sx={{
                    position: 'relative',
                    py: 2,
                    ...sx
                }}
            >
                {messages.map((message) => (
                    <CoreAlert
                        sx={{ mb: 2 }}
                        key={message.id}
                        severity={message.type}
                        action={
                            message.closable ? (
                                <IconButton
                                    onClick={() =>
                                        removeMessage({ id: message.id })
                                    }
                                >
                                    <LeafIcon icon={'xmark'} />
                                </IconButton>
                            ) : undefined
                        }
                    >
                        <CoreTypography variant="body1" color="text.primary">
                            {message.content}
                        </CoreTypography>
                    </CoreAlert>
                ))}

                {!showLoading && backNavigation && (
                    <BackNavigation
                        title={backNavigation.title}
                        action={backNavigation.action}
                    />
                )}

                {title && (
                    <Box
                        sx={{
                            mb: theme.spacing(2)
                        }}
                    >
                        <Grid container>
                            <Grid
                                item
                                md={action ? 8 : 12}
                                sm={12}
                                width="100%"
                            >
                                {showLoading ? (
                                    <Skeleton
                                        sx={{
                                            height: 80,
                                            width: '30%'
                                        }}
                                        variant="text"
                                    />
                                ) : (
                                    <Stack
                                        flexWrap="wrap"
                                        direction={'row'}
                                        sx={{
                                            mt: !backNavigation
                                                ? theme.spacing(1)
                                                : 0,
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Stack
                                            justifyContent="flex-start"
                                            alignItems="flex-start"
                                        >
                                            <CoreTypography
                                                variant="h1"
                                                color={
                                                    theme.palette.text.primary
                                                }
                                            >
                                                {title}
                                            </CoreTypography>
                                        </Stack>

                                        {iconAction && (
                                            <Stack
                                                justifyContent="flex-start"
                                                alignItems="flex-end"
                                            >
                                                {iconAction}
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                                {subTitle && (
                                    <>
                                        {showLoading ? (
                                            <Skeleton
                                                sx={{
                                                    height: 20,
                                                    width: '70%',
                                                    [theme.breakpoints.down(
                                                        'sm'
                                                    )]: {
                                                        width: '90%'
                                                    }
                                                }}
                                                variant="text"
                                            />
                                        ) : (
                                            <CoreTypography
                                                variant="body1"
                                                color="text.primary"
                                                sx={{
                                                    mt: theme.spacing(1)
                                                }}
                                            >
                                                {subTitle}
                                            </CoreTypography>
                                        )}
                                    </>
                                )}
                            </Grid>
                            {action && (
                                <Grid
                                    display={'flex'}
                                    alignItems="center"
                                    justifyContent="end"
                                    item
                                    md={4}
                                    sm={12}
                                    sx={{
                                        [theme.breakpoints.up('md')]: {
                                            pl: theme.spacing(1)
                                        },
                                        [theme.breakpoints.down('md')]: {
                                            mt: theme.spacing(2),
                                            width: '100%'
                                        }
                                    }}
                                >
                                    {action}
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
                {children}
            </Box>
        </PageLayout>
    )
}

function BackNavigation({
    title,
    action
}: {
    title: string
    action?: () => void
}) {
    const theme = useTheme()

    const { isSmallScreen } = usePageContext()

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                mb: theme.spacing(2),
                [theme.breakpoints.between('sm', 'lg')]: {
                    mt: theme.spacing(-2),
                    height: '50px'
                }
            }}
        >
            <CoreTypography
                aria-label={title}
                role="link"
                tabIndex={0}
                onClick={action}
                onKeyDown={ifEnter(action)}
                component="p"
                variant="h5"
                color={theme.palette.primary.main}
                sx={{
                    zIndex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: action ? 'pointer' : undefined
                }}
            >
                {isSmallScreen && (
                    <Icon
                        fontSize="large"
                        sx={{
                            zIndex: 3,
                            display: 'block',
                            width: '35px',
                            height: '35px',
                            pl: '8px',
                            pt: '6px',
                            borderRadius: '50%',
                            color: 'primary.main',
                            backgroundColor: 'primary.contrastText'
                        }}
                    >
                        arrow_back_ios
                    </Icon>
                )}
                {!isSmallScreen && (
                    <Icon
                        fontSize="large"
                        sx={{
                            mr: 1,
                            display: 'block'
                        }}
                    >
                        keyboard_backspace
                    </Icon>
                )}
                <Box
                    sx={{
                        [theme.breakpoints.down('sm')]: {
                            display: 'none'
                        }
                    }}
                    component="span"
                >
                    {title}
                </Box>
            </CoreTypography>
        </Box>
    )
}
